/**
 * The Venue Analytics table view: one row per played night, sortable by any
 * column. "vs venue avg" compares the night's team count with that venue's
 * mean over the selected time range.
 */
import { useMemo, useState } from 'react';
import { Table, Text } from '@radix-ui/themes';
import {
  AttendancePoint,
  VenueRef,
  colorForVenue,
  formatDate,
  formatValue,
  groupByVenue,
  valueFor,
} from './attendanceMath';

type SortKey = 'date' | 'venue' | 'event' | 'teams' | 'people' | 'avg' | 'delta';

const COLUMNS: ReadonlyArray<{ key: SortKey; label: string; numeric: boolean }> = [
  { key: 'date', label: 'Date', numeric: false },
  { key: 'venue', label: 'Venue', numeric: false },
  { key: 'event', label: 'Game night', numeric: false },
  { key: 'teams', label: 'Teams', numeric: true },
  { key: 'people', label: 'People', numeric: true },
  { key: 'avg', label: 'Per team', numeric: true },
  { key: 'delta', label: 'vs venue avg', numeric: true },
];

interface Row {
  point: AttendancePoint;
  delta: number;
}

function sortValue(r: Row, key: SortKey): number | string {
  switch (key) {
    case 'date':
      return new Date(r.point.date).getTime();
    case 'venue':
      return r.point.venueName;
    case 'event':
      return r.point.eventLabel;
    case 'teams':
      return r.point.teamCount;
    case 'people':
      return r.point.peopleCount;
    case 'avg':
      return valueFor(r.point, 'avg');
    case 'delta':
      return r.delta;
    default:
      return 0;
  }
}

function formatDelta(d: number): string {
  const rounded = Math.round(d * 10) / 10;
  if (rounded === 0) {
    return '0';
  }
  return `${rounded > 0 ? '+' : '−'}${Math.abs(rounded).toFixed(1)}`;
}

interface Props {
  points: AttendancePoint[];
  venues: VenueRef[];
}

export default function AttendanceTable({ points, venues }: Props): JSX.Element {
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDesc, setSortDesc] = useState(true);

  const rows = useMemo<Row[]>(() => {
    const meanTeams = new Map<number, number>();
    for (const [venueId, pts] of groupByVenue(points).entries()) {
      meanTeams.set(venueId, pts.reduce((s, p) => s + p.teamCount, 0) / pts.length);
    }
    const out = points.map((point) => ({
      point,
      delta: point.teamCount - (meanTeams.get(point.venueId) ?? point.teamCount),
    }));
    out.sort((a, b) => {
      const va = sortValue(a, sortKey);
      const vb = sortValue(b, sortKey);
      const cmp = typeof va === 'string' && typeof vb === 'string' ? va.localeCompare(vb) : Number(va) - Number(vb);
      return sortDesc ? -cmp : cmp;
    });
    return out;
  }, [points, sortKey, sortDesc]);

  function onSort(key: SortKey, numeric: boolean): void {
    if (key === sortKey) {
      setSortDesc((d) => !d);
      return;
    }
    setSortKey(key);
    // Numbers and dates read best largest/newest first. Names read best A→Z.
    setSortDesc(numeric || key === 'date');
  }

  return (
    <Table.Root variant="surface" size="1" className="attendance-graph__table">
      <Table.Header>
        <Table.Row>
          {COLUMNS.map((c) => {
            const active = c.key === sortKey;
            return (
              <Table.ColumnHeaderCell
                key={c.key}
                justify={c.numeric ? 'end' : 'start'}
                aria-sort={active ? (sortDesc ? 'descending' : 'ascending') : 'none'}
              >
                <button type="button" className="attendance-graph__sort" onClick={() => onSort(c.key, c.numeric)}>
                  {c.label}
                  <span className="attendance-graph__sort-arrow" aria-hidden>
                    {active ? (sortDesc ? '↓' : '↑') : ''}
                  </span>
                </button>
              </Table.ColumnHeaderCell>
            );
          })}
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {rows.map(({ point: p, delta }) => (
          <Table.Row key={p.eventId}>
            <Table.RowHeaderCell style={{ whiteSpace: 'nowrap' }}>{formatDate(p.date)}</Table.RowHeaderCell>
            <Table.Cell style={{ whiteSpace: 'nowrap' }}>
              <span
                className="attendance-graph__swatch attendance-graph__swatch--inline"
                style={{ background: colorForVenue(p.venueId, venues) }}
              />
              {p.venueName}
            </Table.Cell>
            <Table.Cell>{p.eventLabel}</Table.Cell>
            <Table.Cell justify="end">{p.teamCount}</Table.Cell>
            <Table.Cell justify="end">{p.peopleCount}</Table.Cell>
            <Table.Cell justify="end">{formatValue(valueFor(p, 'avg'), 'avg')}</Table.Cell>
            <Table.Cell justify="end">
              <Text color={delta >= 0.05 ? 'green' : delta <= -0.05 ? 'red' : 'gray'}>{formatDelta(delta)}</Text>
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  );
}
